import React, { Children, memo } from 'react';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useScreenInit } from '../useScreenInit';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PRODUCT_NAME } from '../constants/site';
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-10" data-id="element-203">
      <h2 className="text-xl font-bold tracking-tight text-slate-900" data-id="element-204">
        {title}
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600" data-id="element-205">
        {children}
      </div>
    </section>;
}
const PERMS = [{
  name: 'activeTab',
  why: 'Capture the visible area of the X.com tab only after you click the toolbar icon.'
}, {
  name: 'scripting',
  why: 'Inject the small piece of code that scrolls the post into view while stitching long captures.'
}, {
  name: 'downloads',
  why: 'Let you save the resulting PNG to your local disk via the save dialog.'
}, {
  name: 'clipboardWrite',
  why: 'Let you copy the resulting PNG to the system clipboard.'
}, {
  name: 'Content script on x.com / twitter.com',
  why: 'Render the picker UI and result dialog on those sites.'
}];
export function Privacy() {
  useScreenInit();
  return <div className="flex min-h-screen w-full flex-col bg-white" data-id="element-206">
      <Navbar data-id="element-207" />
      <main className="flex-1 bg-slate-50" data-id="element-208">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-id="element-209">
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900" data-id="element-210">
            <ArrowBackIcon sx={{
            fontSize: 18
          }} data-id="element-211" />{' '}
            Back to home
          </Link>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-10" data-id="element-212">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-id="element-213">
              {PRODUCT_NAME} Privacy Policy
            </h1>
            <p className="mt-2 text-sm text-slate-400" data-id="element-214">
              Last updated: May 23, 2026
            </p>

            <p className="mt-6 text-sm leading-relaxed text-slate-600" data-id="element-215">
              {PRODUCT_NAME} is a Chrome browser extension that lets you capture
              screenshots of individual posts on X.com (formerly Twitter).
            </p>

            <div className="mt-6 rounded-xl border border-[#1d9bf0]/30 bg-[#1d9bf0]/5 p-4" data-id="element-216">
              <p className="text-sm font-semibold text-slate-900" data-id="element-217">TL;DR</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-700" data-id="element-218">
                {PRODUCT_NAME} does not collect, store, transmit, or share any
                personal data. All screenshots are created and remain on your
                own device. The extension never contacts any server controlled
                by the developer.
              </p>
            </div>

            <Section title="Data the extension handles" data-id="element-219">
              <p data-id="element-220">
                When you click the {PRODUCT_NAME} toolbar icon and then pick a
                post:
              </p>
              <ol className="list-decimal space-y-2 pl-5" data-id="element-221">
                <li data-id="element-222">
                  The extension captures the visible portion of the current
                  X.com / twitter.com tab and stitches together a PNG image of
                  the chosen post.
                </li>
                <li data-id="element-223">
                  The resulting image is shown to you in a dialog on the same
                  page.
                </li>
                <li data-id="element-224">
                  You may <strong data-id="element-225">Download</strong> the image to your local disk
                  or <strong data-id="element-226">Copy</strong> it to your system clipboard. Both
                  operations are local to your device.
                </li>
              </ol>
              <p data-id="element-227">
                The image is held in memory only for as long as the result
                dialog is open and is discarded when you close it.
              </p>
            </Section>

            <Section title="What the extension does NOT do" data-id="element-228">
              <ul className="list-disc space-y-2 pl-5" data-id="element-229">
                <li data-id="element-230">
                  It does <strong data-id="element-231">not</strong> upload, send, or transmit any
                  image, URL, post content, account information, or browsing
                  data to the developer or any third party.
                </li>
                <li data-id="element-232">
                  It does <strong data-id="element-233">not</strong> include any analytics, telemetry,
                  advertising, tracking pixels, or fingerprinting.
                </li>
                <li data-id="element-234">
                  It does <strong data-id="element-235">not</strong> use cookies or any remote
                  storage.
                </li>
                <li data-id="element-236">
                  It does <strong data-id="element-237">not</strong> read or store the content of any
                  web page outside of the single screenshot you explicitly
                  capture.
                </li>
              </ul>
            </Section>

            <Section title="Permissions and why they are required" data-id="element-238">
              <div className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200" data-id="element-239">
                {PERMS.map(p => <div key={p.name} className="flex flex-col gap-1 bg-white p-4 sm:flex-row sm:gap-4" data-id="element-240">
                    <code className="w-fit shrink-0 rounded-md bg-[#1d9bf0]/10 px-2 py-0.5 text-xs font-semibold text-[#1d9bf0]" data-id="element-241">
                      {p.name}
                    </code>
                    <span className="text-sm text-slate-600" data-id="element-242">{p.why}</span>
                  </div>)}
              </div>
              <p data-id="element-243">
                The extension does <strong data-id="element-244">not</strong> request{' '}
                <code className="rounded bg-slate-100 px-1 py-0.5 text-xs" data-id="element-245">
                  &lt;all_urls&gt;
                </code>{' '}
                or access to any other websites.
              </p>
            </Section>

            <Section title="Children's privacy" data-id="element-246">
              <p data-id="element-247">
                {PRODUCT_NAME} does not knowingly collect any information from
                anyone, including children under 13.
              </p>
            </Section>

            <Section title="Changes to this policy" data-id="element-248">
              <p data-id="element-249">
                If this policy changes in a way that affects what data is
                handled, the update will be reflected in a new release of the
                extension and the “Last updated” date above.
              </p>
            </Section>

            <Section title="Contact" data-id="element-250">
              <p data-id="element-251">
                For questions about this policy or the extension, please open an
                issue on the project's source repository.
              </p>
            </Section>
          </div>
        </div>
      </main>
      <Footer data-id="element-252" />
    </div>;
}